import { Eye, Maximize2, Minimize2 } from "lucide-react";
import type { PreviewStatus } from "./usePreviewIntegration";

interface PreviewToolbarProps {
  status: PreviewStatus;
  isStale: boolean;
  generatedAt: Date | null;
  idleSecondsRemaining: number | null;
  pageInfo?: string;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
}

function formatRelative(date: Date | null): string {
  if (!date) return "never";
  const ms = Date.now() - date.getTime();
  if (ms < 60_000) return "just now";
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  return `${hours}h ago`;
}

function statusLabel(status: PreviewStatus, isStale: boolean): {
  text: string;
  color: string;
} {
  if (status === "generating") return { text: "generating", color: "text-tide" };
  if (status === "failed") return { text: "failed", color: "text-rust" };
  if (isStale || status === "stale") return { text: "stale", color: "text-spark" };
  if (status === "ready") return { text: "current", color: "text-sage" };
  return { text: "no preview yet", color: "text-ink-4" };
}

export function PreviewToolbar({
  status,
  isStale,
  generatedAt,
  idleSecondsRemaining,
  pageInfo,
  isFullscreen,
  onToggleFullscreen,
}: PreviewToolbarProps) {
  const label = statusLabel(status, isStale);

  return (
    <div className="px-5 py-2.5 hair-b border-b bg-paper/80 backdrop-blur sticky top-0 z-10">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[11.5px] text-ink-4">
          <Eye size={13} strokeWidth={1.8} />
          <span>Preview</span>
          <span className="text-ink-5">·</span>
          <span className={`${label.color} font-medium`}>{label.text}</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-[10.5px] text-ink-4 leading-tight text-right whitespace-nowrap">
            <div>
              Last preview ·{" "}
              <span className="num">{formatRelative(generatedAt)}</span>
            </div>
            {idleSecondsRemaining != null && idleSecondsRemaining > 0 && (
              <div className="text-ink-5">
                Auto in{" "}
                <span className="num text-tide">{idleSecondsRemaining}s</span>{" "}
                idle
              </div>
            )}
          </div>
          {onToggleFullscreen && (
            <button
              type="button"
              onClick={onToggleFullscreen}
              aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen preview"}
              title={isFullscreen ? "Exit fullscreen (Esc)" : "Fullscreen preview"}
              className="text-ink-4 hover:text-ink-2 hair border rounded p-1 bg-paper-hi"
            >
              {isFullscreen ? (
                <Minimize2 size={13} strokeWidth={1.8} />
              ) : (
                <Maximize2 size={13} strokeWidth={1.8} />
              )}
            </button>
          )}
        </div>
      </div>
      {pageInfo && (
        <div className="mt-1 text-[11px] text-ink-4">{pageInfo}</div>
      )}
    </div>
  );
}
